// =====================================================
// TAB ESTABLECIMIENTOS - CON EDICIÓN
// src/pages/Agro/Productor/Establecimientos.js
// =====================================================
// MODIFICACIONES:
// - Nuevo botón "Editar" en columna de acciones
// - Modal de edición con datos pre-cargados
// - Usa PUT /api/agro/establecimientos/{id}
// =====================================================

import React, { useState, useEffect, useRef } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Alert,
  Input,
} from "reactstrap"
import DataTable from "react-data-table-component"
import { opcionesPaginacion } from "components/Tables/paginacion"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import classnames from "classnames"

import {
  getEstablecimientos,
  createEstablecimiento,
  updateEstablecimiento,
  deleteEstablecimiento,
  getEstablecimiento,
} from "api/agro"
import {
  getProvincias,
  getDepartamentosPorProvincia,
  getLocalidadesPorProvinciaYDepartamento,
  getLocalidadesPorCodigoPostal,
  asociarCpALocalidad,
} from "api/agro"
import EditorPoligonos from "../Establecimiento/EditorPoligonos"
import DatosSuelo from "../Establecimiento/DatosSuelo"
import GraficoNdvi from "../Establecimiento/GraficoNdvi"
import VisorImagenesSatelitales from "../Establecimiento/VisorImagenesSatelitales"
import EditorParcelas from "../Establecimiento/EditorParcelas"
import DatosClimaticos from "../Establecimiento/DatosClimaticos"
import InformacionSatelitalLote from "../Establecimiento/InformacionSatelitalLote"
import toastHandler from "toastHandler"
import { formatearNumero } from "helpers/formatters"
import { getErrorMessage } from "helpers/error_helpers"
import { labelUsoLote } from "constants/usosLote"
import {
  CampoDestino,
  SeccionSuperficie,
  superficieYupFields,
  AYUDA_DESTINO_EDICION,
  MSG_DESTINO_REQUERIDO,
} from "./CamposEstablecimiento"
import userStore from "userStore"
import useAuthorization from "hooks/useAuthorization"

const RENSPA_REGEX = /^\d{2}\.\d{3}\.\d\.\d{5}\/\d{2}$/
const RENSPA_MSG =
  "Formato inválido. Debe ser XX.XXX.X.XXXXX/XX (ej: 11.222.3.44444/55)"

// BDSJEA-252: misma regla para el formulario y para el popup de alta de CP.
const CP_REGEX = /^\d{4}$/
const CP_MSG = "Debe ser exactamente 4 dígitos numéricos (ej: 5000)"

// Objeto de permisos que exige el backend para asociar un CP a una localidad
// (RolesAgro.EDITAR_TABLAS = modificacion sobre /tablasgenerales).
const OBJETO_TABLAS_GENERALES = "/tablasgenerales"
const URL_DIVISION_GEOGRAFICA = `${process.env.PUBLIC_URL || ""}/divisiongeografica`

const CAMPOS_DOMICILIO = [
  "codigoProvincia",
  "departamento",
  "localidad",
  "codigoPostal",
  "latitud",
  "longitud",
]

// BDSJEA-252: abrir y cerrar un combo sin elegir nada no debe dejar pegado el
// "requerido". Solo se marca touched si hay valor; el submit igual marca todo.
const blurSoloConValor = setFieldTouched => e => {
  if (e.target.value) setFieldTouched(e.target.name, true)
}

// BDSJEA-252: aviso de código postal inexistente, con link a División
// Geográfica (pestaña nueva, para no perder lo cargado) y, si hay localidad
// elegida y permiso sobre /tablasgenerales, popup para asociarle el CP.
const AvisoCpInexistente = ({
  codigo,
  localidad,
  puedeAsociar,
  onAsociado,
}) => {
  const [abierto, setAbierto] = useState(false)
  const [nuevoCp, setNuevoCp] = useState(codigo)
  const [errorCp, setErrorCp] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const setError = toastHandler(state => state.setError)

  const abrir = () => {
    setNuevoCp(codigo)
    setErrorCp(null)
    setAbierto(true)
  }

  const cerrar = () => setAbierto(false)

  const guardar = async () => {
    const cp = (nuevoCp || "").trim()
    if (!CP_REGEX.test(cp)) {
      setErrorCp(CP_MSG)
      return
    }
    setGuardando(true)
    try {
      await asociarCpALocalidad(localidad.id, cp)
    } catch (error) {
      setError(
        getErrorMessage(error, { prefix: "Error al asociar el código postal" }),
      )
      setGuardando(false)
      return
    }
    // Al asociar, el aviso (y este componente) se desmonta: no tocar más estado.
    setAbierto(false)
    onAsociado(cp)
  }

  return (
    <>
      <Alert color="warning" className="py-2 mt-2 mb-0" fade={false}>
        <small>
          <i className="mdi mdi-alert me-1" />
          El código postal {codigo} no existe en la base.
          {localidad && puedeAsociar ? (
            <> Podés asociarlo a {localidad.nombre} o cargarlo en </>
          ) : (
            " Podés cargarlo en "
          )}
          <a
            href={URL_DIVISION_GEOGRAFICA}
            target="_blank"
            rel="noopener noreferrer"
          >
            División Geográfica
          </a>
          .
        </small>
        {localidad && puedeAsociar && (
          <div className="mt-2">
            <Button type="button" size="sm" color="warning" onClick={abrir}>
              <i className="mdi mdi-plus me-1" />
              Asociar CP a {localidad.nombre}
            </Button>
          </div>
        )}
      </Alert>
      <Modal isOpen={abierto} toggle={cerrar} size="sm" zIndex={1070}>
        <ModalHeader toggle={cerrar}>Asociar código postal</ModalHeader>
        <ModalBody>
          <p className="small text-muted mb-2">
            Localidad: <strong>{localidad?.nombre}</strong>
          </p>
          <label className="form-label" htmlFor="nuevo-cp-localidad">
            Código postal
          </label>
          <Input
            id="nuevo-cp-localidad"
            value={nuevoCp}
            invalid={!!errorCp}
            placeholder="Ej: 5000"
            onChange={e => {
              setNuevoCp(e.target.value)
              setErrorCp(null)
            }}
          />
          {errorCp && <div className="text-danger small">{errorCp}</div>}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button type="button" color="secondary" onClick={cerrar}>
              Cancelar
            </Button>
            <Button
              type="button"
              color="primary"
              disabled={guardando}
              onClick={guardar}
            >
              {guardando ? <Spinner size="sm" /> : "Asociar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

const EstablecimientoSchema = Yup.object().shape({
  nombre: Yup.string().required("Nombre requerido"),
  renspa: Yup.string().matches(RENSPA_REGEX, RENSPA_MSG),
  codigoProvincia: Yup.string().required("Provincia requerida"),
  departamento: Yup.string().required("Departamento requerido"),
  localidad: Yup.string().required("Localidad requerida"),
  codigoPostal: Yup.string()
    .required("Código postal requerido")
    .matches(CP_REGEX, CP_MSG),
  destino: Yup.string().required(MSG_DESTINO_REQUERIDO),
  ...superficieYupFields,
  latitud: Yup.number().min(-90).max(90),
  longitud: Yup.number().min(-180).max(180),
})

// Schema de edición - todos los campos editables
const EstablecimientoEditSchema = Yup.object().shape({
  nombre: Yup.string().required("Nombre requerido"),
  renspa: Yup.string().matches(RENSPA_REGEX, RENSPA_MSG),
  codigoProvincia: Yup.string().required("Provincia requerida"),
  departamento: Yup.string().required("Departamento requerido"),
  localidad: Yup.string().required("Localidad requerida"),
  codigoPostal: Yup.string()
    .required("Código postal requerido")
    .matches(CP_REGEX, CP_MSG),
  destino: Yup.string().nullable(),
  ...superficieYupFields,
  latitud: Yup.number().min(-90).max(90).nullable(),
  longitud: Yup.number().min(-180).max(180).nullable(),
})

// BDSJEA-466: la grilla muestra UNA FILA POR DESTINO (uso + id de origen), derivada de los lotes de cada establecimiento
// (EstablecimientoListDTO.destinos). Es solo una vista: el click abre el mismo detalle del establecimiento y la edición y el
// borrado siguen a nivel establecimiento. Un establecimiento sin `destinos` (respuesta de un backend anterior, o sin lotes ni
// datos) aporta una única fila con sus propias columnas. Los campos improductivos (CAMPOS) van aparte, al final.
const USO_IMPRODUCTIVO = "CAMPOS"

export const construirFilasDestino = lista => {
  const filas = []
  ;(lista || []).forEach(est => {
    const destinos =
      est.destinos && est.destinos.length > 0
        ? est.destinos
        : [
            {
              uso: est.destino,
              usoDescripcion: est.destinoDescripcion,
              idOrigen: null,
              superficiePropiaHa: est.superficiePropiaHa,
              superficieArrendadaHa: est.superficieArrendadaHa,
              superficieTotalHa: est.superficieTotalHa,
              improductivo: est.destino === USO_IMPRODUCTIVO,
            },
          ]
    destinos.forEach((d, i) => {
      filas.push({
        key: `${est.id}-${i}`,
        establecimiento: est,
        idOrigen: d.idOrigen,
        uso: d.uso,
        destino: d.usoDescripcion || labelUsoLote(d.uso),
        propia: d.superficiePropiaHa,
        arrendada: d.superficieArrendadaHa,
        sinTenencia: d.superficieSinTenenciaHa,
        total: d.superficieTotalHa,
        cantidadLotes:
          d.cantidadLotes != null ? d.cantidadLotes : est.cantidadPoligonos,
        improductivo: !!d.improductivo || d.uso === USO_IMPRODUCTIVO,
        origenPlanProductivo: !!d.origenPlanProductivo,
      })
    })
  })
  return [
    ...filas.filter(f => !f.improductivo),
    ...filas.filter(f => f.improductivo),
  ]
}

// BDSJEA-466: hectáreas con 2 decimales solo cuando hay fracción (así la suma visible coincide con las filas).
export const formatearHa = v =>
  formatearNumero(v, v != null && Math.abs(v - Math.round(v)) >= 0.005 ? 2 : 0)

// BDSJEA-497: texto del nivel de rendimiento («Rinde Muy Bueno»); «-» si el establecimiento no tiene nivel (mismo criterio que el reporte).
export const nivelRendimientoEstablecimiento = est =>
  (est && est.nivelRendimientoDescripcion) ||
  (est && est.idNivelRendimiento != null ? String(est.idNivelRendimiento) : "-")

// BDSJEA-497: orden por el código del catálogo (1 Excelente … 5 Bajo); las filas sin nivel van siempre al final del orden ascendente.
export const compararNivelRendimiento = (a, b) => {
  const ca = a.establecimiento.idNivelRendimiento
  const cb = b.establecimiento.idNivelRendimiento
  if (ca == null && cb == null) return 0
  if (ca == null) return 1
  if (cb == null) return -1
  return ca - cb
}

const sumar = (filas, campo) =>
  filas.reduce((acc, f) => acc + (Number(f[campo]) || 0), 0)

export const totalesDestino = filas => {
  const productivas = filas.filter(f => !f.improductivo)
  const propias = sumar(productivas, "propia")
  const arrendadas = sumar(productivas, "arrendada")
  const sinTenencia = sumar(productivas, "sinTenencia")
  const totales = sumar(productivas, "total")
  return {
    propias,
    arrendadas,
    sinTenencia,
    totales,
    pctPropias: totales > 0 ? (propias * 100) / totales : null,
    pctArrendadas: totales > 0 ? (arrendadas * 100) / totales : null,
    campos: sumar(
      filas.filter(f => f.improductivo),
      "total",
    ),
  }
}

const Establecimientos = ({ idProductor, establecimientoId }) => {
  const [loading, setLoading] = useState(true)
  const [establecimientos, setEstablecimientos] = useState([])
  const [provincias, setProvincias] = useState([])
  const [departamentos, setDepartamentos] = useState([])
  const [localidades, setLocalidades] = useState([])
  const [cpsDisponibles, setCpsDisponibles] = useState([])

  // Modals
  const [modalNuevo, setModalNuevo] = useState(false)
  const [modalDetalle, setModalDetalle] = useState(false)
  const [modalEditar, setModalEditar] = useState(false)
  const [establecimientoSeleccionado, setEstablecimientoSeleccionado] =
    useState(null)
  const [poligonosEstablecimiento, setPoligonosEstablecimiento] = useState([])

  // Tabs del modal detalle
  const [activeTab, setActiveTab] = useState("poligonos")

  const setSuccess = toastHandler(state => state.setSuccess)
  const setError = toastHandler(state => state.setError)
  const modificacion = userStore(state => state.modificacion)
  const { puedeModificarObjeto } = useAuthorization()
  const puedeAsociarCp = puedeModificarObjeto(OBJETO_TABLAS_GENERALES)

  // BDSJEA-252: CP buscado que no existe en la base (para mostrar el aviso).
  const [cpNoEncontrado, setCpNoEncontrado] = useState(null)

  useEffect(() => {
    loadData()
    loadCatalogos()
  }, [idProductor])

  const hasAutoOpenedFromParam = useRef(false)

  useEffect(() => {
    if (
      !hasAutoOpenedFromParam.current &&
      establecimientoId &&
      establecimientos.length > 0
    ) {
      const est = establecimientos.find(e => e.id === Number(establecimientoId))
      if (est) {
        hasAutoOpenedFromParam.current = true
        abrirDetalle(est)
      }
    }
  }, [establecimientoId, establecimientos])

  const loadData = async () => {
    setLoading(true)
    try {
      const data = await getEstablecimientos(idProductor)
      setEstablecimientos(data)
    } catch (error) {
      setError(
        getErrorMessage(error, { prefix: "Error al cargar establecimientos" }),
      )
    } finally {
      setLoading(false)
    }
  }

  const loadCatalogos = async () => {
    try {
      const provs = await getProvincias()
      setProvincias(provs)
    } catch (error) {
      console.error("Error cargando catálogos:", error)
    }
  }

  const cargarDepartamentos = async (codigoProvincia, setFieldValue) => {
    try {
      const deps = await getDepartamentosPorProvincia(codigoProvincia)
      setDepartamentos(deps)
      setLocalidades([])
      setCpsDisponibles([])
      if (setFieldValue) {
        setFieldValue("departamento", "")
        setFieldValue("localidad", "")
        setFieldValue("codigoPostal", "")
      }
    } catch (error) {
      console.error("Error cargando departamentos:", error)
      setDepartamentos([])
    }
  }

  const cargarLocalidades = async (
    codigoProvincia,
    departamento,
    setFieldValue,
  ) => {
    try {
      const locs = await getLocalidadesPorProvinciaYDepartamento(
        codigoProvincia,
        departamento,
      )
      setLocalidades(locs)
      const cps = [
        ...new Set(locs.flatMap(l => l.codigosPostales || []).filter(Boolean)),
      ]
      setCpsDisponibles(cps)
      if (setFieldValue) {
        setFieldValue("localidad", "")
        setFieldValue("codigoPostal", "")
      }
      return locs
    } catch (error) {
      console.error("Error cargando localidades:", error)
      setLocalidades([])
      setCpsDisponibles([])
      return []
    }
  }

  const buscarPorCodigoPostal = async (cp, setFieldValue) => {
    if (!cp || cp.length < 4) return
    try {
      const resultados = await getLocalidadesPorCodigoPostal(cp)
      if (resultados && resultados.length > 0) {
        setCpNoEncontrado(null)
        // Limpiar los campos de dirección antes de autocompletarlos
        setFieldValue("departamento", "")
        setFieldValue("localidad", "")
        setFieldValue("latitud", "")
        setFieldValue("longitud", "")
        setDepartamentos([])
        setLocalidades([])
        setCpsDisponibles([])

        const primera = resultados[0]

        // 1. Setear provincia y cargar departamentos
        if (primera.codigoProvincia) {
          setFieldValue("codigoProvincia", primera.codigoProvincia)
          const deps = await getDepartamentosPorProvincia(
            primera.codigoProvincia,
          )
          setDepartamentos(deps)
        }

        // 2. Setear departamento y cargar localidades
        if (primera.departamento && primera.codigoProvincia) {
          setFieldValue("departamento", primera.departamento)
          const locs = await getLocalidadesPorProvinciaYDepartamento(
            primera.codigoProvincia,
            primera.departamento,
          )
          setLocalidades(locs)
          const cps = [
            ...new Set(
              locs.flatMap(l => l.codigosPostales || []).filter(Boolean),
            ),
          ]
          setCpsDisponibles(cps)
        }

        // 3. Setear localidad
        if (primera.nombre) {
          setFieldValue("localidad", primera.nombre)
        }

        // 4. Coordenadas
        if (primera.latitud && primera.longitud) {
          setFieldValue("latitud", primera.latitud.toString())
          setFieldValue("longitud", primera.longitud.toString())
        }
      } else if (CP_REGEX.test(cp)) {
        // BDSJEA-252: el CP no existe. No se toca lo ya elegido (la localidad
        // seleccionada sirve para ofrecer asociarle el CP).
        setCpNoEncontrado(cp)
      }
    } catch (error) {
      console.error("Error buscando por código postal:", error)
    }
  }

  // BDSJEA-252: localidad elegida en el formulario (con id, para poder
  // asociarle un CP). null si todavía no se eligió o el catálogo no trae id.
  const localidadSeleccionada = values =>
    (values.localidad &&
      localidades.find(
        l =>
          l.nombre === values.localidad &&
          l.departamento === values.departamento &&
          l.id != null,
      )) ||
    null

  // BDSJEA-252: el CP recién asociado queda seleccionado en el establecimiento.
  const handleCpAsociado = (codigo, localidad, setFieldValue) => {
    setLocalidades(prev =>
      prev.map(l =>
        l.id === localidad.id
          ? {
              ...l,
              codigosPostales: [
                ...new Set([...(l.codigosPostales || []), codigo]),
              ],
            }
          : l,
      ),
    )
    setCpsDisponibles(prev => [...new Set([...prev, codigo])])
    setCpNoEncontrado(null)
    setFieldValue("codigoPostal", codigo)
    setSuccess(`Código postal ${codigo} asociado a ${localidad.nombre}`)
  }

  // BDSJEA-252: "Limpiar domicilio" resetea valores Y touched de provincia y
  // todos sus dependientes, para que no quede ningún "requerido" pegado.
  const limpiarDomicilio = (setFieldValue, setTouched, touched) => {
    CAMPOS_DOMICILIO.forEach(campo => setFieldValue(campo, ""))
    const touchedLimpio = { ...touched }
    CAMPOS_DOMICILIO.forEach(campo => {
      touchedLimpio[campo] = false
    })
    setTouched(touchedLimpio, false)
    setDepartamentos([])
    setLocalidades([])
    setCpsDisponibles([])
    setCpNoEncontrado(null)
  }

  const renderAvisoCp = (values, setFieldValue) => {
    if (!cpNoEncontrado || cpNoEncontrado !== values.codigoPostal) return null
    const localidad = localidadSeleccionada(values)
    return (
      <AvisoCpInexistente
        key={cpNoEncontrado}
        codigo={cpNoEncontrado}
        localidad={localidad}
        puedeAsociar={puedeAsociarCp}
        onAsociado={codigo =>
          handleCpAsociado(codigo, localidad, setFieldValue)
        }
      />
    )
  }

  const handleCrear = async (values, { setSubmitting }) => {
    try {
      await createEstablecimiento({
        ...values,
        idProductor,
        destino: values.destino,
        superficiePropiaHa: Number(values.superficiePropiaHa),
        superficieArrendadaHa: Number(values.superficieArrendadaHa),
        latitud: values.latitud ? parseFloat(values.latitud) : null,
        longitud: values.longitud ? parseFloat(values.longitud) : null,
        departamento: values.departamento || null,
        codigoPostal: values.codigoPostal || null,
      })

      setSuccess("Establecimiento creado correctamente")
      setModalNuevo(false)
      setDepartamentos([])
      setLocalidades([])
      loadData()
    } catch (error) {
      setError(getErrorMessage(error, { prefix: "Error al crear" }))
    } finally {
      setSubmitting(false)
    }
  }

  // =====================================================
  // NUEVO: Handler para editar establecimiento
  // =====================================================
  const handleEditar = async (values, { setSubmitting }) => {
    try {
      await updateEstablecimiento(establecimientoSeleccionado.id, {
        nombre: values.nombre,
        renspa: values.renspa,
        codigoProvincia: values.codigoProvincia || null,
        localidad: values.localidad || null,
        departamento: values.departamento || null,
        codigoPostal: values.codigoPostal || null,
        destino: values.destino || undefined,
        superficiePropiaHa: Number(values.superficiePropiaHa),
        superficieArrendadaHa: Number(values.superficieArrendadaHa),
        latitud: values.latitud ? parseFloat(values.latitud) : null,
        longitud: values.longitud ? parseFloat(values.longitud) : null,
      })

      setSuccess("Establecimiento actualizado correctamente")
      setModalEditar(false)
      setEstablecimientoSeleccionado(null)
      setDepartamentos([])
      setLocalidades([])
      loadData()
    } catch (error) {
      setError(getErrorMessage(error, { prefix: "Error al actualizar" }))
      // Solo si falla: al guardar bien, el formulario ya se desmontó.
      setSubmitting(false)
    }
  }

  // NUEVO: Abrir modal de edición con datos del establecimiento
  const abrirEdicion = async establecimiento => {
    try {
      const detalle = await getEstablecimiento(establecimiento.id)
      setEstablecimientoSeleccionado(detalle)
      setModalEditar(true)
      const codigoProvincia = detalle.codigoProvincia || detalle.idProvincia
      if (codigoProvincia) {
        await cargarDepartamentos(codigoProvincia, null)
        if (detalle.departamento) {
          const locs = await cargarLocalidades(
            codigoProvincia,
            detalle.departamento,
            null,
          )
          if (detalle.localidad && locs) {
            const cpsLoc = [
              ...new Set(
                locs
                  .filter(
                    l =>
                      l.nombre === detalle.localidad &&
                      l.departamento === detalle.departamento,
                  )
                  .flatMap(l => l.codigosPostales || [])
                  .filter(Boolean),
              ),
            ]
            setCpsDisponibles(cpsLoc)
          }
        }
      }
    } catch {
      setEstablecimientoSeleccionado(establecimiento)
      setModalEditar(true)
    }
  }

  const abrirDetalle = async establecimiento => {
    setEstablecimientoSeleccionado(establecimiento)
    setActiveTab("poligonos")
    setModalDetalle(true)

    try {
      const detalle = await getEstablecimiento(establecimiento.id)
      setEstablecimientoSeleccionado(detalle)
      setPoligonosEstablecimiento(detalle.poligonos || [])
    } catch (error) {
      console.error("Error cargando detalle:", error)
      setPoligonosEstablecimiento([])
    }
  }

  // BDSJEA-462: editar un lote recalcula superficie agrícola/ganadera y actividad principal del establecimiento (y puede
  // reclasificar al productor): se recarga el detalle abierto y la lista.
  const refrescarTrasEditarLote = async () => {
    try {
      const detalle = await getEstablecimiento(establecimientoSeleccionado.id)
      setEstablecimientoSeleccionado(detalle)
      setPoligonosEstablecimiento(detalle.poligonos || [])
    } catch (error) {
      setError(
        getErrorMessage(error, {
          prefix:
            "El lote se guardó pero no se pudo actualizar el establecimiento",
        }),
      )
    }
    loadData()
  }

  const handleEliminar = async (id, nombre) => {
    if (
      !window.confirm(
        nombre
          ? `¿Está seguro que desea eliminar el establecimiento "${nombre}" (todos sus destinos y lotes)?`
          : "¿Está seguro que desea eliminar este establecimiento?",
      )
    )
      return
    try {
      await deleteEstablecimiento(id)
      setSuccess("Establecimiento eliminado correctamente")
      loadData()
    } catch (error) {
      setError(
        getErrorMessage(error, { prefix: "Error al eliminar establecimiento" }),
      )
    }
  }

  // BDSJEA-466: una fila por destino. Ver/Editar/Eliminar actúan sobre el establecimiento de la fila.
  const filas = construirFilasDestino(establecimientos)
  const totales = totalesDestino(filas)

  const columns = [
    {
      name: "Id",
      selector: row => row.idOrigen,
      sortable: true,
      width: "90px",
      format: row =>
        row.idOrigen != null
          ? row.idOrigen
          : row.origenPlanProductivo
            ? "Plan productivo"
            : "-",
    },
    ...[
      { name: "Has Propias", campo: "propia" },
      { name: "Has Arrendadas", campo: "arrendada" },
      { name: "Has Totales", campo: "total" },
    ].map(({ name, campo }) => ({
      name,
      selector: row => row[campo],
      sortable: true,
      right: true,
      width: "150px",
      format: row => formatearHa(row[campo]),
    })),
    {
      name: "Destino",
      selector: row => row.destino,
      sortable: true,
      width: "130px",
    },
    {
      name: "Localidad",
      selector: row => row.establecimiento.localidad,
      sortable: true,
      width: "150px",
    },
    {
      name: "Departamento",
      selector: row => row.establecimiento.departamento,
      sortable: true,
      width: "170px",
    },
    {
      name: "Provincia",
      selector: row => row.establecimiento.provincia,
      sortable: true,
      width: "140px",
    },
    {
      // BDSJEA-497: nivel de rendimiento del establecimiento (catálogo TABLASGENERALES 147), igual que el reporte del Plan
      // Productivo (BDSJEA-470). NO es la zona agropecuaria (nombreZona): esa sigue en el DTO para valor_agro y la proyección.
      name: "Rendimiento",
      selector: row => row.establecimiento.idNivelRendimiento,
      sortable: true,
      sortFunction: compararNivelRendimiento,
      format: row => nivelRendimientoEstablecimiento(row.establecimiento),
      grow: 2,
      minWidth: "180px",
    },
    {
      // Lotes del destino (de la fila); sin `destinos` (backend anterior), los lotes del establecimiento.
      name: "Lotes",
      selector: row => row.cantidadLotes,
      center: true,
      width: "100px",
      cell: row => (
        <Badge color={row.cantidadLotes > 0 ? "success" : "secondary"}>
          {row.cantidadLotes || 0}
        </Badge>
      ),
    },
    {
      // Tres botones: Ver + Editar + Eliminar (a nivel establecimiento)
      name: "Acciones",
      center: true,
      width: "140px",
      ignoreRowClick: true,
      cell: row => (
        <div className="d-flex gap-1">
          <Button
            size="sm"
            color="primary"
            onClick={() => abrirDetalle(row.establecimiento)}
            title="Ver detalle"
            aria-label="Ver detalle"
          >
            <i className="mdi mdi-eye" />
          </Button>
          {modificacion && (
            <Button
              size="sm"
              color="warning"
              outline
              onClick={() => abrirEdicion(row.establecimiento)}
              title="Editar establecimiento"
            >
              <i className="mdi mdi-pencil" />
            </Button>
          )}
          {modificacion && (
            <Button
              size="sm"
              color="danger"
              outline
              onClick={() =>
                handleEliminar(
                  row.establecimiento.id,
                  row.establecimiento.nombre,
                )
              }
              title="Eliminar establecimiento"
            >
              <i className="mdi mdi-trash-can" />
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              <i className="mdi mdi-map-marker me-2" />
              Establecimientos
            </h5>
            {modificacion && (
              <Button color="primary" onClick={() => setModalNuevo(true)}>
                <i className="mdi mdi-plus me-1" />
                Nuevo Establecimiento
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : establecimientos.length === 0 ? (
            <Alert color="info" fade={false}>
              <i className="mdi mdi-information me-2" />
              No hay establecimientos registrados. Agregue el primer
              establecimiento.
            </Alert>
          ) : (
            <>
              <DataTable
                columns={columns}
                data={filas}
                keyField="key"
                pagination
                paginationPerPage={10}
                {...opcionesPaginacion(filas.length)}
                highlightOnHover
                pointerOnHover
                onRowClicked={row => abrirDetalle(row.establecimiento)}
                noDataComponent="No hay establecimientos registrados"
              />
              <div className="mt-2 small" data-testid="totales-destino">
                <div>
                  <b>Totales:</b> Has Propias {formatearHa(totales.propias)} (
                  {totales.pctPropias == null
                    ? "-"
                    : `${formatearNumero(totales.pctPropias, 1)} %`}
                  ) · Has Arrendadas {formatearHa(totales.arrendadas)} (
                  {totales.pctArrendadas == null
                    ? "-"
                    : `${formatearNumero(totales.pctArrendadas, 1)} %`}
                  ) · Has Totales {formatearHa(totales.totales)}
                </div>
                {totales.campos > 0 && (
                  <div className="text-muted">
                    Campos improductivos: {formatearHa(totales.campos)} ha
                    (aparte, no incluidas en los totales).
                  </div>
                )}
                {totales.sinTenencia > 0 && (
                  <div className="text-muted">
                    {formatearHa(totales.sinTenencia)} ha sin tenencia cargada
                    figuran solo en Has Totales.
                  </div>
                )}
                <div className="text-muted">
                  Una fila por destino, derivada de los lotes de cada
                  establecimiento. Editar y eliminar actúan sobre el
                  establecimiento completo.
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* =====================================================
          MODAL NUEVO ESTABLECIMIENTO (sin cambios)
          ===================================================== */}
      <Modal isOpen={modalNuevo} toggle={() => setModalNuevo(false)} size="lg">
        <ModalHeader toggle={() => setModalNuevo(false)}>
          <i className="mdi mdi-plus-circle text-primary me-2" />
          Nuevo Establecimiento
        </ModalHeader>
        <Formik
          initialValues={{
            nombre: "",
            renspa: "",
            codigoProvincia: "",
            localidad: "",
            departamento: "",
            codigoPostal: "",
            destino: "",
            superficiePropiaHa: "",
            superficieArrendadaHa: 0,
            latitud: "",
            longitud: "",
          }}
          validationSchema={EstablecimientoSchema}
          onSubmit={handleCrear}
        >
          {({
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            setTouched,
            values,
            validateForm,
          }) => (
            <Form>
              <ModalBody>
                <div className="border rounded p-3 mb-3">
                  <h6 className="text-muted mb-3">
                    <i className="mdi mdi-file-document-outline me-1" />
                    Datos del Establecimiento
                  </h6>
                  <Row>
                    <Col md={7}>
                      <div className="mb-3">
                        <label className="form-label">
                          Nombre del Establecimiento *
                        </label>
                        <Field
                          name="nombre"
                          className="form-control"
                          placeholder="Ej: Estancia Santa Rosa"
                        />
                        {touched.nombre && errors.nombre && (
                          <div className="text-danger small">
                            {errors.nombre}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={5}>
                      <div className="mb-3">
                        <label className="form-label">RENSPA</label>
                        <Field
                          name="renspa"
                          className={`form-control${touched.renspa && errors.renspa ? " is-invalid" : ""}`}
                          placeholder="XX.XXX.X.XXXXX/XX"
                        />
                        {touched.renspa && errors.renspa && (
                          <div className="text-danger small">
                            {errors.renspa}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={7}>
                      <CampoDestino touched={touched} errors={errors} />
                    </Col>
                  </Row>
                </div>

                <div className="border rounded p-3 mb-3">
                  <h6 className="text-muted mb-3">
                    <i className="mdi mdi-map-marker-outline me-1" />
                    Domicilio
                  </h6>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label">Provincia *</label>
                        <Field
                          name="codigoProvincia"
                          as="select"
                          onBlur={blurSoloConValor(setFieldTouched)}
                          className={`form-control${touched.codigoProvincia && errors.codigoProvincia ? " is-invalid" : ""}`}
                          onChange={e => {
                            const codigo = e.target.value
                            setFieldValue("codigoProvincia", codigo)
                            if (codigo) {
                              cargarDepartamentos(codigo, setFieldValue)
                            } else {
                              setDepartamentos([])
                              setLocalidades([])
                              setFieldValue("departamento", "")
                              setFieldValue("localidad", "")
                              setFieldValue("codigoPostal", "")
                            }
                          }}
                        >
                          <option value="">Seleccione...</option>
                          {provincias.map(p => (
                            <option key={p.codigo} value={p.codigo}>
                              {p.descripcion}
                            </option>
                          ))}
                        </Field>
                        {touched.codigoProvincia && errors.codigoProvincia && (
                          <div className="text-danger small">
                            {errors.codigoProvincia}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label">
                          Departamento / Partido
                        </label>
                        <Field
                          name="departamento"
                          as="select"
                          onBlur={blurSoloConValor(setFieldTouched)}
                          className={`form-control${touched.departamento && errors.departamento ? " is-invalid" : ""}`}
                          disabled={!values.codigoProvincia}
                          onChange={e => {
                            const depto = e.target.value
                            setFieldValue("departamento", depto)
                            if (depto && values.codigoProvincia) {
                              cargarLocalidades(
                                values.codigoProvincia,
                                depto,
                                setFieldValue,
                              )
                            } else {
                              setLocalidades([])
                              setFieldValue("localidad", "")
                              setFieldValue("codigoPostal", "")
                            }
                          }}
                        >
                          <option value="">Seleccione...</option>
                          {departamentos.map(d => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </Field>
                        {touched.departamento && errors.departamento && (
                          <div className="text-danger small">
                            {errors.departamento}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label">Localidad *</label>
                        <Field
                          name="localidad"
                          as="select"
                          onBlur={blurSoloConValor(setFieldTouched)}
                          className={`form-control${touched.localidad && errors.localidad ? " is-invalid" : ""}`}
                          disabled={!values.departamento}
                          onChange={e => {
                            const loc = e.target.value
                            setFieldValue("localidad", loc)
                            setCpNoEncontrado(null)
                            if (loc) {
                              const locsFiltradas = localidades.filter(
                                l =>
                                  l.nombre === loc &&
                                  l.departamento === values.departamento,
                              )
                              const cpsLoc = [
                                ...new Set(
                                  locsFiltradas
                                    .flatMap(l => l.codigosPostales || [])
                                    .filter(Boolean),
                                ),
                              ]
                              setCpsDisponibles(cpsLoc)
                              const primera = locsFiltradas[0]
                              if (primera) {
                                if (cpsLoc.length > 0) {
                                  setFieldValue("codigoPostal", cpsLoc[0])
                                } else {
                                  setFieldValue("codigoPostal", "")
                                }
                                if (primera.latitud && primera.longitud) {
                                  setFieldValue(
                                    "latitud",
                                    primera.latitud.toString(),
                                  )
                                  setFieldValue(
                                    "longitud",
                                    primera.longitud.toString(),
                                  )
                                }
                              }
                            } else {
                              const cps = [
                                ...new Set(
                                  localidades
                                    .flatMap(l => l.codigosPostales || [])
                                    .filter(Boolean),
                                ),
                              ]
                              setCpsDisponibles(cps)
                              setFieldValue("codigoPostal", "")
                            }
                          }}
                        >
                          <option value="">Seleccione...</option>
                          {[...new Set(localidades.map(l => l.nombre))].map(
                            nombre => (
                              <option key={nombre} value={nombre}>
                                {nombre}
                              </option>
                            ),
                          )}
                        </Field>
                        {touched.localidad && errors.localidad && (
                          <div className="text-danger small">
                            {errors.localidad}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label">Código Postal *</label>
                        {cpsDisponibles.length > 0 ? (
                          <Field
                            as="select"
                            name="codigoPostal"
                            className={`form-control${touched.codigoPostal && errors.codigoPostal ? " is-invalid" : ""}`}
                            onChange={e => {
                              const cp = e.target.value
                              setFieldValue("codigoPostal", cp)
                              const locMatch = localidades.find(
                                l =>
                                  (l.codigosPostales || []).includes(cp) &&
                                  l.nombre === values.localidad,
                              )
                              if (
                                locMatch &&
                                locMatch.latitud &&
                                locMatch.longitud
                              ) {
                                setFieldValue(
                                  "latitud",
                                  locMatch.latitud.toString(),
                                )
                                setFieldValue(
                                  "longitud",
                                  locMatch.longitud.toString(),
                                )
                              }
                            }}
                          >
                            <option value="">Seleccione...</option>
                            {cpsDisponibles.map(cp => (
                              <option key={cp} value={cp}>
                                {cp}
                              </option>
                            ))}
                          </Field>
                        ) : (
                          <Field
                            name="codigoPostal"
                            className={`form-control${touched.codigoPostal && errors.codigoPostal ? " is-invalid" : ""}`}
                            placeholder="Ej: 5000"
                            onBlur={e => {
                              const cp = e.target.value
                              if (cp) {
                                buscarPorCodigoPostal(cp, setFieldValue)
                              }
                            }}
                            onChange={e => {
                              setFieldValue("codigoPostal", e.target.value)
                              setCpNoEncontrado(null)
                            }}
                          />
                        )}
                        {touched.codigoPostal && errors.codigoPostal && (
                          <div className="text-danger small">
                            {errors.codigoPostal}
                          </div>
                        )}
                        {renderAvisoCp(values, setFieldValue)}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label">Latitud</label>
                        <Field
                          name="latitud"
                          type="number"
                          step="0.0000001"
                          className="form-control"
                          placeholder="-34.123456"
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label">Longitud</label>
                        <Field
                          name="longitud"
                          type="number"
                          step="0.0000001"
                          className="form-control"
                          placeholder="-58.123456"
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end">
                    <Button
                      type="button"
                      color="light"
                      outline
                      onClick={() =>
                        limpiarDomicilio(setFieldValue, setTouched, touched)
                      }
                    >
                      <i className="mdi mdi-broom me-1" />
                      Limpiar domicilio
                    </Button>
                  </div>
                </div>

                <div className="border rounded p-3 mb-3">
                  <h6 className="text-muted mb-3">
                    <i className="mdi mdi-vector-square me-1" />
                    Superficie
                  </h6>
                  <SeccionSuperficie
                    values={values}
                    touched={touched}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                  />
                </div>

                <Alert color="info" className="py-2" fade={false}>
                  <small>
                    <i className="mdi mdi-information me-1" />
                    Al crear el primer establecimiento del productor, se
                    generará automáticamente una parcela y un lote por default
                    (con el uso elegido como destino). Podrá modificarlos
                    después desde la pestaña de detalle.
                  </small>
                </Alert>
              </ModalBody>
              <div className="modal-footer">
                <Button color="secondary" onClick={() => setModalNuevo(false)}>
                  Cancelar
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  onClick={async e => {
                    const validationErrors = await validateForm()
                    if (Object.keys(validationErrors).length > 0) {
                      e.preventDefault()
                      const campos = Object.keys(validationErrors)
                        .map(k => validationErrors[k])
                        .join("; ")
                      setError("Datos incompletos: " + campos)
                    }
                  }}
                >
                  {isSubmitting ? <Spinner size="sm" /> : "Crear"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* =====================================================
          NUEVO: MODAL EDITAR ESTABLECIMIENTO
          ===================================================== */}
      <Modal
        isOpen={modalEditar}
        toggle={() => setModalEditar(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setModalEditar(false)}>
          <i className="mdi mdi-pencil text-warning me-2" />
          Editar Establecimiento
        </ModalHeader>
        {establecimientoSeleccionado && (
          <Formik
            enableReinitialize
            initialValues={{
              nombre: establecimientoSeleccionado.nombre || "",
              renspa: establecimientoSeleccionado.renspa || "",
              codigoProvincia:
                establecimientoSeleccionado.codigoProvincia ||
                establecimientoSeleccionado.idProvincia ||
                "",
              localidad: establecimientoSeleccionado.localidad || "",
              departamento: establecimientoSeleccionado.departamento || "",
              codigoPostal: establecimientoSeleccionado.codigoPostal || "",
              destino: establecimientoSeleccionado.destino || "",
              superficiePropiaHa:
                establecimientoSeleccionado.superficiePropiaHa ?? "",
              superficieArrendadaHa:
                establecimientoSeleccionado.superficieArrendadaHa ?? "",
              latitud: establecimientoSeleccionado.latitud || "",
              longitud: establecimientoSeleccionado.longitud || "",
            }}
            validationSchema={EstablecimientoEditSchema}
            onSubmit={handleEditar}
          >
            {({
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              setTouched,
              values,
              validateForm,
            }) => (
              <Form>
                <ModalBody>
                  <div className="border rounded p-3 mb-3">
                    <h6 className="text-muted mb-3">
                      <i className="mdi mdi-file-document-outline me-1" />
                      Datos del Establecimiento
                    </h6>
                    <Row>
                      <Col md={8}>
                        <div className="mb-3">
                          <label className="form-label">
                            Nombre del Establecimiento *
                          </label>
                          <Field name="nombre" className="form-control" />
                          {touched.nombre && errors.nombre && (
                            <div className="text-danger small">
                              {errors.nombre}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <label className="form-label">RENSPA</label>
                          <Field
                            name="renspa"
                            className={`form-control${touched.renspa && errors.renspa ? " is-invalid" : ""}`}
                            placeholder="XX.XXX.X.XXXXX/XX"
                          />
                          {touched.renspa && errors.renspa && (
                            <div className="text-danger small">
                              {errors.renspa}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={8}>
                        <CampoDestino
                          touched={touched}
                          errors={errors}
                          ayuda={AYUDA_DESTINO_EDICION}
                        />
                      </Col>
                    </Row>
                  </div>

                  <div className="border rounded p-3 mb-3">
                    <h6 className="text-muted mb-3">
                      <i className="mdi mdi-map-marker-outline me-1" />
                      Domicilio
                    </h6>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Provincia *</label>
                          <Field
                            name="codigoProvincia"
                            as="select"
                            onBlur={blurSoloConValor(setFieldTouched)}
                            className={`form-control${touched.codigoProvincia && errors.codigoProvincia ? " is-invalid" : ""}`}
                            onChange={e => {
                              const codigo = e.target.value
                              setFieldValue("codigoProvincia", codigo)
                              if (codigo) {
                                cargarDepartamentos(codigo, setFieldValue)
                              } else {
                                setDepartamentos([])
                                setLocalidades([])
                                setFieldValue("departamento", "")
                                setFieldValue("localidad", "")
                                setFieldValue("codigoPostal", "")
                              }
                            }}
                          >
                            <option value="">Seleccione...</option>
                            {provincias.map(p => (
                              <option key={p.codigo} value={p.codigo}>
                                {p.descripcion}
                              </option>
                            ))}
                          </Field>
                          {touched.codigoProvincia &&
                            errors.codigoProvincia && (
                              <div className="text-danger small">
                                {errors.codigoProvincia}
                              </div>
                            )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">
                            Departamento / Partido
                          </label>
                          <Field
                            name="departamento"
                            as="select"
                            onBlur={blurSoloConValor(setFieldTouched)}
                            className={`form-control${touched.departamento && errors.departamento ? " is-invalid" : ""}`}
                            disabled={!values.codigoProvincia}
                            onChange={e => {
                              const depto = e.target.value
                              setFieldValue("departamento", depto)
                              if (depto && values.codigoProvincia) {
                                cargarLocalidades(
                                  values.codigoProvincia,
                                  depto,
                                  setFieldValue,
                                )
                              } else {
                                setLocalidades([])
                                setFieldValue("localidad", "")
                                setFieldValue("codigoPostal", "")
                              }
                            }}
                          >
                            <option value="">Seleccione...</option>
                            {departamentos.map(d => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ))}
                          </Field>
                          {touched.departamento && errors.departamento && (
                            <div className="text-danger small">
                              {errors.departamento}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Localidad *</label>
                          <Field
                            name="localidad"
                            as="select"
                            onBlur={blurSoloConValor(setFieldTouched)}
                            className={`form-control${touched.localidad && errors.localidad ? " is-invalid" : ""}`}
                            disabled={!values.departamento}
                            onChange={e => {
                              const loc = e.target.value
                              setFieldValue("localidad", loc)
                              setCpNoEncontrado(null)
                              if (loc) {
                                const locsFiltradas = localidades.filter(
                                  l =>
                                    l.nombre === loc &&
                                    l.departamento === values.departamento,
                                )
                                const cpsLoc = [
                                  ...new Set(
                                    locsFiltradas
                                      .flatMap(l => l.codigosPostales || [])
                                      .filter(Boolean),
                                  ),
                                ]
                                setCpsDisponibles(cpsLoc)
                                const primera = locsFiltradas[0]
                                if (primera) {
                                  if (cpsLoc.length > 0) {
                                    setFieldValue("codigoPostal", cpsLoc[0])
                                  } else {
                                    setFieldValue("codigoPostal", "")
                                  }
                                  if (primera.latitud && primera.longitud) {
                                    setFieldValue(
                                      "latitud",
                                      primera.latitud.toString(),
                                    )
                                    setFieldValue(
                                      "longitud",
                                      primera.longitud.toString(),
                                    )
                                  }
                                }
                              } else {
                                const cps = [
                                  ...new Set(
                                    localidades
                                      .flatMap(l => l.codigosPostales || [])
                                      .filter(Boolean),
                                  ),
                                ]
                                setCpsDisponibles(cps)
                                setFieldValue("codigoPostal", "")
                              }
                            }}
                          >
                            <option value="">Seleccione...</option>
                            {[...new Set(localidades.map(l => l.nombre))].map(
                              nombre => (
                                <option key={nombre} value={nombre}>
                                  {nombre}
                                </option>
                              ),
                            )}
                          </Field>
                          {touched.localidad && errors.localidad && (
                            <div className="text-danger small">
                              {errors.localidad}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Código Postal *</label>
                          {cpsDisponibles.length > 0 ? (
                            <Field
                              as="select"
                              name="codigoPostal"
                              className={`form-control${touched.codigoPostal && errors.codigoPostal ? " is-invalid" : ""}`}
                              onChange={e => {
                                const cp = e.target.value
                                setFieldValue("codigoPostal", cp)
                                const locMatch = localidades.find(
                                  l =>
                                    (l.codigosPostales || []).includes(cp) &&
                                    l.nombre === values.localidad,
                                )
                                if (
                                  locMatch &&
                                  locMatch.latitud &&
                                  locMatch.longitud
                                ) {
                                  setFieldValue(
                                    "latitud",
                                    locMatch.latitud.toString(),
                                  )
                                  setFieldValue(
                                    "longitud",
                                    locMatch.longitud.toString(),
                                  )
                                }
                              }}
                            >
                              <option value="">Seleccione...</option>
                              {cpsDisponibles.map(cp => (
                                <option key={cp} value={cp}>
                                  {cp}
                                </option>
                              ))}
                            </Field>
                          ) : (
                            <Field
                              name="codigoPostal"
                              className={`form-control${touched.codigoPostal && errors.codigoPostal ? " is-invalid" : ""}`}
                              placeholder="Ej: 5000"
                              onBlur={e => {
                                const cp = e.target.value
                                if (cp) {
                                  buscarPorCodigoPostal(cp, setFieldValue)
                                }
                              }}
                              onChange={e => {
                                setFieldValue("codigoPostal", e.target.value)
                                setCpNoEncontrado(null)
                              }}
                            />
                          )}
                          {touched.codigoPostal && errors.codigoPostal && (
                            <div className="text-danger small">
                              {errors.codigoPostal}
                            </div>
                          )}
                          {renderAvisoCp(values, setFieldValue)}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Latitud</label>
                          <Field
                            name="latitud"
                            type="number"
                            step="0.0000001"
                            className="form-control"
                            placeholder="-34.123456"
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Longitud</label>
                          <Field
                            name="longitud"
                            type="number"
                            step="0.0000001"
                            className="form-control"
                            placeholder="-58.123456"
                          />
                        </div>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end">
                      <Button
                        type="button"
                        color="light"
                        outline
                        onClick={() =>
                          limpiarDomicilio(setFieldValue, setTouched, touched)
                        }
                      >
                        <i className="mdi mdi-broom me-1" />
                        Limpiar domicilio
                      </Button>
                    </div>
                  </div>

                  <div className="border rounded p-3 mb-3">
                    <h6 className="text-muted mb-3">
                      <i className="mdi mdi-vector-square me-1" />
                      Superficie
                    </h6>
                    <SeccionSuperficie
                      values={values}
                      touched={touched}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />
                  </div>
                </ModalBody>
                <div className="modal-footer">
                  <Button
                    color="secondary"
                    onClick={() => setModalEditar(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    color="primary"
                    type="submit"
                    disabled={isSubmitting}
                    onClick={async e => {
                      const validationErrors = await validateForm()
                      if (Object.keys(validationErrors).length > 0) {
                        e.preventDefault()
                        const campos = Object.keys(validationErrors)
                          .map(k => validationErrors[k])
                          .join("; ")
                        setError("Datos incompletos: " + campos)
                      }
                    }}
                  >
                    {isSubmitting ? <Spinner size="sm" /> : "Guardar Cambios"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      {/* =====================================================
          MODAL DETALLE (sin cambios)
          ===================================================== */}
      <Modal
        isOpen={modalDetalle}
        toggle={() => setModalDetalle(false)}
        size="xl"
        style={{ maxWidth: "95%" }}
      >
        <ModalHeader toggle={() => setModalDetalle(false)}>
          {establecimientoSeleccionado?.nombre}
          {establecimientoSeleccionado?.renspa && (
            <Badge color="secondary" className="ms-2">
              RENSPA: {establecimientoSeleccionado.renspa}
            </Badge>
          )}
        </ModalHeader>
        <ModalBody>
          {establecimientoSeleccionado && (
            <>
              <style>
                {`
                .nav-tabs-establecimiento .nav-link.active {
                  color: #495057 !important;
                  background-color: #fff !important;
                  font-weight: 600 !important;
                  border-color: #dee2e6 #dee2e6 #fff !important;
                }
                .nav-tabs-establecimiento .nav-link {
                  color: #6c757d !important;
                  cursor: pointer;
                }
                .nav-tabs-establecimiento .nav-link:hover:not(.active) {
                  color: #495057 !important;
                  border-color: #e9ecef #e9ecef #dee2e6 !important;
                }
              `}
              </style>
              <Nav tabs className="nav-tabs-establecimiento mb-3">
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: activeTab === "poligonos",
                    })}
                    onClick={() => setActiveTab("poligonos")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="mdi mdi-map me-1" />
                    Polígonos/Lotes
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: activeTab === "infoSatelital",
                    })}
                    onClick={() => setActiveTab("infoSatelital")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="mdi mdi-satellite-variant me-1" />
                    <strong>Info Satelital</strong>
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "clima" })}
                    onClick={() => setActiveTab("clima")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="mdi mdi-weather-partly-cloudy me-1" />
                    Clima
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "parcelas" })}
                    onClick={() => setActiveTab("parcelas")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="mdi mdi-file-document-multiple me-1" />
                    Parcelas
                  </NavLink>
                </NavItem>
              </Nav>

              {activeTab === "infoSatelital" && (
                <InformacionSatelitalLote
                  idEstablecimiento={establecimientoSeleccionado.id}
                  establecimiento={establecimientoSeleccionado}
                />
              )}

              {activeTab === "parcelas" && (
                <EditorParcelas
                  idEstablecimiento={establecimientoSeleccionado.id}
                  establecimiento={establecimientoSeleccionado}
                  readonly={!modificacion}
                />
              )}

              {activeTab === "poligonos" && (
                <EditorPoligonos
                  idEstablecimiento={establecimientoSeleccionado.id}
                  establecimiento={establecimientoSeleccionado}
                  readonly={!modificacion}
                  onLoteActualizado={refrescarTrasEditarLote}
                />
              )}

              {activeTab === "imagenes" && (
                <VisorImagenesSatelitales
                  idEstablecimiento={establecimientoSeleccionado.id}
                  poligonos={poligonosEstablecimiento}
                />
              )}

              {activeTab === "suelo" && (
                <DatosSuelo
                  idEstablecimiento={establecimientoSeleccionado.id}
                />
              )}

              {activeTab === "ndvi" && (
                <GraficoNdvi
                  idEstablecimiento={establecimientoSeleccionado.id}
                />
              )}

              {activeTab === "clima" && (
                <DatosClimaticos
                  idEstablecimiento={establecimientoSeleccionado.id}
                />
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export default Establecimientos
